<script setup>
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useInfiniteScroll} from "@vueuse/core";
import {computed, ref} from "vue";
import AnnotationRow from "@/components/admin/seo-score-annotations/partials/table/AnnotationRow.vue";
import SearchInput from "@/components/global-themed/SearchInput.vue";
import {useAnnotationFilters, useAnnotations} from "@/composables/admin/useSeoScoreAnnotations";
import AnnotationTableHeader from "@/components/admin/seo-score-annotations/partials/table/AnnotationTableHeader.vue";
import AnnotationModal from "@/components/admin/seo-score-annotations/partials/AnnotationModal.vue";

const tableBody = ref(null);
const {search} = useAnnotationFilters();

const {
  query,
  annotations,
  visibleRows,
} = useAnnotations();

const { isLoading } = query;

const showModal = ref(false);
const selectedAnnotation = ref(null);

const openCreateModal = () => {
  selectedAnnotation.value = null;
  showModal.value = true;
};

const openEditModal = (annotation) => {
  selectedAnnotation.value = annotation;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedAnnotation.value = null;
};

useInfiniteScroll(
  computed(() => tableBody.value?.$el),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 10,
  }
);
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-flex-col tw-gap-y-8">
      <div>
        <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
          Global SEO Score Annotations
        </h1>

        <p class="tw-text-neutral tw-text-base">
          Manage global SEO score annotations that appear on all user dashboards. These annotations help explain significant changes in SEO scores across the platform.
        </p>
      </div>

      <div
        v-if="isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg"
      >
        <LoadingBlock />
      </div>

      <div
        v-show="!isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8"
      >
        <div class="tw-grid tw-grid-cols-12 tw-gap-4 tw-items-center">
          <div class="tw-col-span-4">
            <SearchInput
              v-model="search"
              placeholder="Search annotations..."
            />
          </div>
          <div class="tw-col-span-8 tw-flex tw-justify-end">
            <DefaultButton
              variant="primary"
              @click="openCreateModal"
            >
              <svg-vue
                icon="icons/plus"
                class="tw-w-6 tw-h-6 tw-mr-3"
              />
              <span class="tw-font-semibold">Create Annotation</span>
            </DefaultButton>
          </div>
        </div>

        <DataTable>
          <AnnotationTableHeader />
          <TableBody
            v-if="annotations?.length > 0"
            ref="tableBody"
            class="tw-max-h-[70vh]"
          >
            <AnnotationRow
              v-for="annotation in annotations"
              :key="annotation.id"
              :annotation="annotation"
              @edit="openEditModal"
            />
          </TableBody>

          <ZeroContent v-if="!annotations || !annotations.length">
            Looks like there are no annotations to display.
          </ZeroContent>
        </DataTable>
      </div>
    </div>

    <AnnotationModal
      v-if="showModal"
      :annotation="selectedAnnotation"
      @close="closeModal"
    />
  </div>
</template>
